// Historial de precios del SKU (REQ-46): tabla compartida por el modal de la cabecera y la pestaña Precio.
import { ErrorState, Modal, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import type { SkuPriceHistory } from '@/api/services/retail-catalog/backoffice';
import { useSkuPrices } from '../../api/useInventorySku';
import { PRICE_SOURCE_LABEL, skuDetailErrorMessage } from '../../lib/inventoryDetail';
import { DETAIL_TD, DETAIL_TH } from './DetailCard';

/** Centavos a pesos; sin monto, «—». */
export function priceOrDash(cents: number | null | undefined): string {
    return cents == null ? '—' : formatMxn(mxn(cents));
}

export interface PriceHistoryQuery {
    data?: SkuPriceHistory;
    error: unknown;
    refetch: () => unknown;
}

export function PriceHistoryContent({ prices }: { prices: PriceHistoryQuery }) {
    const items = prices.data?.items;
    if (prices.error) {
        return (
            <ErrorState
                title="No se pudo cargar el historial de precios"
                description={skuDetailErrorMessage(prices.error)}
                onRetry={() => {
                    void prices.refetch();
                }}
            />
        );
    }
    if (items === undefined) return <Skeleton lines={4} />;
    if (items.length === 0) {
        return (
            <div className="py-5 text-center text-[11px] italic text-muted-foreground">
                Sin precios registrados para este SKU.
            </div>
        );
    }
    return (
        <div className="max-h-[420px] overflow-y-auto rounded-md border border-border">
            <table className="w-full border-collapse text-[11px]">
                <thead>
                    <tr>
                        <th className={DETAIL_TH}>Vigente desde</th>
                        <th className={cn(DETAIL_TH, 'text-right')}>Precio contado</th>
                        <th className={DETAIL_TH}>Origen</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((price, index) => (
                        <tr key={price.effectiveFrom}>
                            <td className={cn(DETAIL_TD, 'whitespace-nowrap tabular-nums')}>
                                {formatDateTimeMx(price.effectiveFrom)}
                                {index === 0 ? (
                                    <span className="ml-1.5 rounded-[3px] bg-success-soft px-1 py-px text-[9.5px] font-bold text-success">
                                        vigente
                                    </span>
                                ) : null}
                            </td>
                            <td className={cn(DETAIL_TD, 'text-right font-bold tabular-nums')}>
                                {priceOrDash(price.basePriceCents)}
                            </td>
                            <td className={DETAIL_TD}>
                                {PRICE_SOURCE_LABEL[price.source] ?? price.source}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function PriceHistoryModal({
    sku,
    title,
    isOpen,
    onClose,
}: {
    sku: string;
    title: string;
    isOpen: boolean;
    onClose: () => void;
}) {
    const prices = useSkuPrices(sku, isOpen);
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Historial de precios · ${title}`}
            size="lg"
        >
            <PriceHistoryContent prices={prices} />
        </Modal>
    );
}
